import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Instagram,
  Facebook,
  Play,
  Plus,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Bug,
  Search,
  Layers,
} from "lucide-react";
import { getEventType } from "@shared/eventTypes";
import { AdminHostEventRow } from "../components/AdminHostEventRow";
import type { AdminHostEvent, HostEventReviewStatus } from "../api/useAdminHostEvents";
import { useReviewHostEvent } from "../api/useAdminHostEvents";
import {
  useAdminSocialCrawlPages,
  useAdminCrawlGroups,
  useAddSocialCrawlPage,
  useAddSocialCrawlPagesBatch,
  useUpdateSocialCrawlPage,
  useDeleteSocialCrawlPage,
  useDebugSocialCrawl,
  useRunSocialCrawl,
  useSaveDebugEvent,
  usePageCrawledEvents,
  type SocialPlatform,
  type SocialCrawlPage,
  type CrawlGroup,
  type ExtractedEventPreview,
  type CrawledPostPreview,
  type CrawlRunResult,
  type CrawlParams,
} from "../api/useAdminSocialCrawling";

const CDN_HOSTS = ["cdninstagram.com", "fbcdn.net", "fbsbx.com", "instagram.com", "facebook.com"];
function proxyCdnUrl(url: string): string {
  try {
    const { hostname } = new URL(url);
    if (CDN_HOSTS.some((h) => hostname.endsWith(h))) {
      return `/api/admin/social-crawling/image-proxy?url=${encodeURIComponent(url)}`;
    }
  } catch { /* not a valid URL */ }
  return url;
}

// ──────────────────────────────────────────────────────────────────────────────
// Small helpers
// ──────────────────────────────────────────────────────────────────────────────

function PlatformIcon({ platform, className }: { platform: SocialPlatform; className?: string }) {
  if (platform === "instagram") return <Instagram className={className ?? "w-4 h-4"} />;
  return <Facebook className={className ?? "w-4 h-4"} />;
}

function PlatformBadge({ platform }: { platform: SocialPlatform }) {
  const isIg = platform === "instagram";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isIg
          ? "bg-pink-500/15 text-pink-400"
          : "bg-blue-500/15 text-blue-400"
      }`}
    >
      <PlatformIcon platform={platform} className="w-3 h-3" />
      {isIg ? "Instagram" : "Facebook"}
    </span>
  );
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? "text-emerald-400" : pct >= 60 ? "text-amber-400" : "text-red-400";
  return <span className={`text-xs font-medium ${color}`}>{pct}% confidence</span>;
}

// ──────────────────────────────────────────────────────────────────────────────
// Add-page form
// ──────────────────────────────────────────────────────────────────────────────

function GroupPickerDialog({
  groups,
  value,
  onChange,
  onClose,
}: {
  groups: CrawlGroup[];
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = query.trim()
    ? groups.filter((g) => g.name.toLowerCase().includes(query.toLowerCase()))
    : groups;

  const select = (id: string) => { onChange(id); onClose(); };

  return (
    <div className="modal modal-open z-[9999]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.15 }}
        className="modal-box relative card max-w-sm p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-dark-border">
          <h3 className="font-semibold text-sm mb-3">Assign team</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted pointer-events-none" />
            <input
              autoFocus
              type="text"
              placeholder="Search teams…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="input input-sm pl-9 w-full"
            />
          </div>
        </div>
        <div className="overflow-y-auto max-h-72 p-2">
          <button
            type="button"
            onClick={() => select("")}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              value === "" ? "bg-primary-500/20 text-primary-400" : "hover:bg-dark-border text-dark-muted"
            }`}
          >
            No team
          </button>
          {filtered.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => select(g.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                value === g.id ? "bg-primary-500/20 text-primary-400" : "hover:bg-dark-border"
              }`}
            >
              {g.imageUrl && (
                <img src={g.imageUrl} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
              )}
              {g.name}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-xs text-dark-muted text-center py-4">No teams match "{query}"</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function GroupPicker({
  groups,
  value,
  onChange,
}: {
  groups: CrawlGroup[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = groups.find((g) => g.id === value);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs border border-dark-border bg-dark-card hover:bg-dark-border transition-colors max-w-[160px] truncate"
      >
        {selected ? (
          <>
            {selected.imageUrl && (
              <img src={selected.imageUrl} alt="" className="w-4 h-4 rounded-full object-cover shrink-0" />
            )}
            <span className="truncate">{selected.name}</span>
          </>
        ) : (
          <span className="text-dark-muted">No team</span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <GroupPickerDialog
            groups={groups}
            value={value}
            onChange={onChange}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function AddPageForm({ onDone }: { onDone: () => void }) {
  const [platform, setPlatform] = useState<SocialPlatform>("instagram");
  const [identifier, setIdentifier] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [groupId, setGroupId] = useState("");
  const add = useAddSocialCrawlPage();
  const { data: groupsData } = useAdminCrawlGroups();
  const groups = groupsData?.data ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    await add.mutateAsync({
      platform,
      pageIdentifier: identifier.trim(),
      displayName: displayName.trim() || undefined,
      groupId: groupId || undefined,
    });
    onDone();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-dark-muted">Platform</span>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
            className="input input-sm"
          >
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-dark-muted">
            {platform === "instagram" ? "Username" : "Page ID or username"}
          </span>
          <input
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder={platform === "instagram" ? "e.g. outclose.nyc" : "e.g. OutcloseNYC"}
            className="input input-sm"
            required
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-dark-muted">Display name (optional)</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Outclose NYC"
            className="input input-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-dark-muted">Team (optional)</span>
          <GroupPicker groups={groups} value={groupId} onChange={setGroupId} />
        </label>
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onDone} className="btn btn-sm btn-ghost">
          Cancel
        </button>
        <button type="submit" disabled={add.isPending} className="btn btn-sm btn-primary">
          {add.isPending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add page
        </button>
      </div>
    </form>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Batch add form
// ──────────────────────────────────────────────────────────────────────────────

function parseIdentifiers(raw: string): string[] {
  return [...new Set(
    raw.split(/[\n,]+/)
      .map((s) => s.trim().replace(/^@/, ""))
      .filter(Boolean),
  )];
}

function BatchAddForm({ onDone }: { onDone: () => void }) {
  const [platform, setPlatform] = useState<SocialPlatform>("instagram");
  const [groupId, setGroupId] = useState("");
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState<{ added: number; skipped: number } | null>(null);
  const batch = useAddSocialCrawlPagesBatch();
  const { data: groupsData } = useAdminCrawlGroups();
  const groups = groupsData?.data ?? [];

  const identifiers = parseIdentifiers(raw);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (identifiers.length === 0) return;
    const res = await batch.mutateAsync({
      platform,
      pageIdentifiers: identifiers,
      groupId: groupId || undefined,
    });
    setResult({ added: res.data?.added ?? 0, skipped: res.data?.skipped ?? 0 });
  };

  if (result) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-emerald-400">Import complete</p>
            <p className="text-xs text-dark-muted mt-0.5">
              {result.added} page{result.added !== 1 ? "s" : ""} added
              {result.skipped > 0 && ` · ${result.skipped} already tracked (skipped)`}
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => { setResult(null); setRaw(""); }} className="btn btn-sm btn-ghost">
            Add more
          </button>
          <button type="button" onClick={onDone} className="btn btn-sm btn-primary">
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-dark-muted">Platform</span>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
            className="input input-sm"
          >
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-dark-muted">Team (optional)</span>
          <GroupPicker groups={groups} value={groupId} onChange={setGroupId} />
        </label>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-dark-muted">
          Usernames — one per line, or comma-separated. Leading @ is stripped automatically.
        </span>
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={"outclose.nyc\noutclose.sf\noutclose.la"}
          rows={6}
          className="input text-sm font-mono resize-y leading-relaxed"
        />
      </label>
      {identifiers.length > 0 && (
        <p className="text-xs text-dark-muted">
          {identifiers.length} unique handle{identifiers.length !== 1 ? "s" : ""} parsed
        </p>
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onDone} className="btn btn-sm btn-ghost">
          Cancel
        </button>
        <button
          type="submit"
          disabled={identifiers.length === 0 || batch.isPending}
          className="btn btn-sm btn-primary"
        >
          {batch.isPending
            ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            : <Layers className="w-3.5 h-3.5" />}
          Import {identifiers.length > 0 ? identifiers.length : ""} page{identifiers.length !== 1 ? "s" : ""}
        </button>
      </div>
    </form>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Crawl params dialog
// ──────────────────────────────────────────────────────────────────────────────

function CrawlParamsDialog({
  page,
  onRun,
  onClose,
}: {
  page: SocialCrawlPage;
  onRun: (params: CrawlParams) => void;
  onClose: () => void;
}) {
  const [maxPosts, setMaxPosts] = useState(10);
  const [sinceMode, setSinceMode] = useState<"last" | "alltime" | "custom">(
    page.lastCrawledAt ? "last" : "alltime",
  );
  const [sinceDate, setSinceDate] = useState(
    page.lastCrawledAt ? page.lastCrawledAt.slice(0, 10) : "",
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onRun({
      maxPosts,
      sinceDate:
        sinceMode === "alltime" ? null :
        sinceMode === "custom" ? (sinceDate || null) :
        page.lastCrawledAt ?? null,
    });
    onClose();
  };

  return (
    <div className="modal modal-open z-[9999]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.15 }}
        className="modal-box relative card max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold mb-4">Crawl settings</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-dark-muted">Max posts to fetch</span>
            <input
              type="number"
              min={1}
              max={100}
              value={maxPosts}
              onChange={(e) => setMaxPosts(Math.min(100, Math.max(1, Number(e.target.value))))}
              className="input input-sm w-full"
            />
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-xs text-dark-muted">Fetch posts since</span>
            <div className="flex flex-col gap-1.5">
              {page.lastCrawledAt && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" className="radio radio-sm" checked={sinceMode === "last"} onChange={() => setSinceMode("last")} />
                  Last crawl ({new Date(page.lastCrawledAt).toLocaleDateString()})
                </label>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" className="radio radio-sm" checked={sinceMode === "alltime"} onChange={() => setSinceMode("alltime")} />
                All time
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" className="radio radio-sm" checked={sinceMode === "custom"} onChange={() => setSinceMode("custom")} />
                Custom date
              </label>
              {sinceMode === "custom" && (
                <input
                  type="date"
                  value={sinceDate}
                  onChange={(e) => setSinceDate(e.target.value)}
                  className="input input-sm w-full mt-1"
                  autoFocus
                />
              )}
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <button type="button" onClick={onClose} className="btn btn-sm btn-ghost">Cancel</button>
            <button type="submit" className="btn btn-sm btn-primary">
              <Play className="w-3.5 h-3.5 mr-1" /> Run crawl
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Debug results panel
// ──────────────────────────────────────────────────────────────────────────────

function CrawlDebugPanel({ result }: { result: CrawlRunResult }) {
  const [open, setOpen] = useState(true);
  const rows: [string, string | number][] = [
    ["Posts fetched", result.postsFound],
    ["Not an event", result.discardedNotEvent],
    ["Low confidence", result.discardedLowConfidence],
    ["Missing date", result.discardedMissingDate],
    ["Missing time", result.discardedMissingTime],
    ["Eligible", result.eligibleEvents],
    ["Venue from cache", result.venueFromCache],
    ["Venue from Maps", result.venueFromMaps],
    ["Venue no Maps result", result.venueNoMapsResult],
    ["Venue city unresolved", result.venueNoCityMatch],
    ["Cover uploaded", result.coverImageUploaded],
    ["Cover failed", result.coverImageFailed],
    ["Events saved", result.eventsSaved],
    ["Events failed", result.eventsSaveFailed],
    ["LLM tokens", `${result.totalTokens.toLocaleString()} (${result.promptTokens.toLocaleString()} + ${result.candidateTokens.toLocaleString()})`],
  ];

  return (
    <div className="mt-2 pt-2 border-t border-dark-border">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="flex items-center gap-1.5 text-xs text-dark-muted hover:text-white transition-colors mb-1"
      >
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        Crawl debug info
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs font-mono bg-dark-card rounded-lg p-3">
          {rows.map(([label, val]) => (
            <div key={label} className="flex justify-between gap-2">
              <span className="text-dark-muted">{label}</span>
              <span className={
                label === "Events saved" && Number(val) > 0 ? "text-emerald-400" :
                (label.includes("failed") || label.includes("unresolved")) && Number(val) > 0 ? "text-amber-400" :
                ""
              }>{val}</span>
            </div>
          ))}
          {result.error && (
            <div className="col-span-2 text-red-400 mt-1">{result.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

// Single crawl-page row
// ──────────────────────────────────────────────────────────────────────────────


function PageRow({ page }: { page: SocialCrawlPage }) {
  const qc = useQueryClient();
  const remove = useDeleteSocialCrawlPage();
  const update = useUpdateSocialCrawlPage();
  const runCrawl = useRunSocialCrawl();
  const { data: groupsData } = useAdminCrawlGroups();
  const groups = groupsData?.data ?? [];
  const [crawlParamsOpen, setCrawlParamsOpen] = useState(false);
  const [crawlResult, setCrawlResult] = useState<string | null>(null);
  const [crawlDebug, setCrawlDebug] = useState<CrawlRunResult | null>(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const { data: eventsData, isLoading: eventsLoading } = usePageCrawledEvents(eventsOpen ? page.id : null);
  const crawledEvents = ((eventsData as any)?.data ?? []) as AdminHostEvent[];
  const review = useReviewHostEvent();
  const handleReview = (id: string, status: HostEventReviewStatus) => {
    review.mutate({ id, reviewStatus: status }, {
      onSuccess: () => {
        qc.setQueryData(["page-crawled-events", page.id], (old: any) => {
          if (!old?.data) return old;
          return { ...old, data: old.data.map((ev: any) => ev.id === id ? { ...ev, reviewStatus: status } : ev) };
        });
      },
    });
  };

  const handleToggle = () => update.mutate({ id: page.id, isActive: !page.isActive });
  const handleDelete = () => {
    if (confirm(`Delete ${page.pageIdentifier}?`)) remove.mutate(page.id);
  };
  const handleCrawl = async (params: CrawlParams) => {
    const res = await runCrawl.mutateAsync({ pageId: page.id, params });
    if (res.success && res.data) {
      const d = res.data;
      setCrawlResult(`${d.postsFound} posts → ${d.eligibleEvents} eligible → ${d.eventsSaved} saved`);
      setCrawlDebug(d);
      qc.invalidateQueries({ queryKey: ["page-crawled-events", page.id] });
      setEventsOpen(true);
    }
  };
  const handleGroupChange = (groupId: string) => {
    update.mutate({ id: page.id, groupId: groupId || null });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`card ${!page.isActive ? "opacity-50" : ""}`}
    >
      <button
        type="button"
        onClick={() => setEventsOpen((o) => !o)}
        className="flex items-start gap-3 w-full text-left cursor-pointer"
      >
        <PlatformIcon platform={page.platform} className="w-5 h-5 mt-0.5 shrink-0 text-dark-muted" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{page.displayName ?? page.pageIdentifier}</span>
            <PlatformBadge platform={page.platform} />
            {!page.isActive && (
              <span className="text-xs text-dark-muted bg-dark-border px-2 py-0.5 rounded-full">paused</span>
            )}
          </div>
          <a
            href={
              page.platform === "instagram"
                ? `https://www.instagram.com/${page.pageIdentifier}`
                : `https://www.facebook.com/${page.pageIdentifier}`
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-dark-muted hover:text-white transition-colors inline-flex items-center gap-1"
          >
            @{page.pageIdentifier}
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
          <div className="flex items-center gap-2 mt-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs text-dark-muted">
              {page.lastCrawledAt
                ? `Last crawled: ${new Date(page.lastCrawledAt).toLocaleDateString()}`
                : "Never crawled"}
            </p>
            <GroupPicker
              groups={groups}
              value={page.groupId ?? ""}
              onChange={handleGroupChange}
            />
          </div>
          {crawlResult && (
            <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> {crawlResult}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 px-2 text-dark-muted">
            <span className="text-xs">{page.eventCount}</span>
            {eventsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          <button
            onClick={() => setCrawlParamsOpen(true)}
            disabled={runCrawl.isPending}
            className="btn btn-ghost btn-sm p-2"
            title="Run crawl now"
          >
            {runCrawl.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={handleToggle}
            className="btn btn-ghost btn-sm p-2"
            title={page.isActive ? "Pause" : "Resume"}
          >
            {page.isActive ? (
              <Clock className="w-4 h-4 text-amber-400" />
            ) : (
              <CheckCircle className="w-4 h-4 text-emerald-400" />
            )}
          </button>
          <button
            onClick={handleDelete}
            disabled={remove.isPending}
            className="btn btn-ghost btn-sm p-2 text-red-400"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </button>

      <AnimatePresence>
        {crawlParamsOpen && (
          <CrawlParamsDialog
            page={page}
            onRun={handleCrawl}
            onClose={() => setCrawlParamsOpen(false)}
          />
        )}
      </AnimatePresence>

      {crawlDebug && <CrawlDebugPanel result={crawlDebug} />}

      {eventsOpen && (
        <div className="mt-2 pt-2 border-t border-dark-border">
          {eventsLoading ? (
            <div className="flex items-center justify-center py-6">
              <RefreshCw className="w-5 h-5 animate-spin text-dark-muted" />
            </div>
          ) : crawledEvents.length === 0 ? (
            <p className="text-xs text-dark-muted py-1">No events sourced from this page yet.</p>
          ) : (
            <div className="space-y-3">
              {crawledEvents.map((ev) => (
                <AdminHostEventRow key={ev.id} event={ev} onReview={handleReview} />
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Extracted event card  (matches HostEvents EventRow style)
// ──────────────────────────────────────────────────────────────────────────────

function CoverThumb({ src }: { src: string }) {
  const ref = useRef<HTMLImageElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const proxied = proxyCdnUrl(src);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 8, left: r.left + window.scrollX });
  };
  return (
    <div className="shrink-0">
      <img
        ref={ref}
        src={proxied}
        alt="cover"
        title={src}
        className="w-14 h-14 rounded-xl object-cover bg-dark-border cursor-pointer"
        onMouseEnter={show}
        onMouseLeave={() => setPos(null)}
      />
      {pos && createPortal(
        <img
          src={proxied}
          alt=""
          style={{ position: "absolute", top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-[480px] h-auto rounded-xl shadow-2xl border border-dark-border pointer-events-none"
        />,
        document.body,
      )}
    </div>
  );
}

function ExtractedEventCard({
  event,
  onSave,
  saving,
  savedId,
}: {
  event: ExtractedEventPreview;
  onSave: (event: ExtractedEventPreview) => void;
  saving: boolean;
  savedId: string | null;
}) {
  if (!event.isEvent) {
    return (
      <div className="card border border-dark-border opacity-60 flex items-center gap-2 py-2 px-3">
        <AlertCircle className="w-4 h-4 text-dark-muted shrink-0" />
        <span className="text-xs text-dark-muted flex-1 truncate">Not an event · {event.sourcePostUrl}</span>
        <ConfidenceBadge value={event.confidence} />
      </div>
    );
  }

  const pct = Math.round(event.confidence * 100);
  const badgeColor = pct >= 80
    ? "bg-emerald-500/15 text-emerald-400"
    : pct >= 60
    ? "bg-amber-500/15 text-amber-400"
    : "bg-red-500/15 text-red-400";

  const alreadyExists = !!event.existingEventId;
  const canSave = !!event.resolvedVenue?.cityId && !alreadyExists && !savedId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="card flex flex-col gap-3"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {(event.coverImageDataUrl ?? event.coverImageUrl) ? (
            <CoverThumb src={event.coverImageDataUrl ?? event.coverImageUrl!} />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-dark-card shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-white leading-snug">{event.name}</h3>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
                {pct}%
              </span>
              {alreadyExists && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400">
                  <CheckCircle className="w-3 h-3" /> exists already
                </span>
              )}
              {savedId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400">
                  <CheckCircle className="w-3 h-3" /> saved
                </span>
              )}
            </div>
            <p className="text-sm text-dark-muted mt-0.5">
              {[
                event.date,
                event.time && `${event.time}${event.endTime ? ` – ${event.endTime}` : ""}`,
                event.venueName,
                event.resolvedVenue?.cityName,
              ].filter(Boolean).join(" · ")}
            </p>
            {event.existingEventName && (
              <p className="text-xs text-blue-400 mt-0.5">Matched: "{event.existingEventName}"</p>
            )}
            {event.description && (
              <p className="text-xs text-dark-muted mt-1.5 line-clamp-2">{event.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={event.sourcePostUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-sm p-2"
            title="View source post"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Types + price row */}
      {(event.types.length > 0 || event.priceCents != null) && (
        <div className="flex items-center gap-2 flex-wrap">
          {event.types.map((t) => {
            const et = getEventType(t);
            return (
              <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-dark-card text-dark-muted">
                {et ? `${et.emoji} ${et.label}` : t}
              </span>
            );
          })}
          {event.priceCents != null && (
            <span className="text-xs text-dark-muted">
              {event.priceCents === 0 ? "Free" : `$${(event.priceCents / 100).toFixed(2)}`}
            </span>
          )}
        </div>
      )}

      {/* Venue footer + save button */}
      <div className="flex items-center gap-3 pt-1 border-t border-dark-border">
        <div className="flex items-start gap-2 text-xs text-dark-muted flex-1 min-w-0">
          <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-400" />
          {event.resolvedVenue ? (
            <span className="truncate">
              <span className="text-emerald-400 font-medium">{event.resolvedVenue.name}</span>
              {event.resolvedVenue.address !== "TBD" && ` · ${event.resolvedVenue.address}`}
              {event.resolvedVenue.cityName
                ? <span className="text-emerald-400"> · {event.resolvedVenue.cityName}</span>
                : <span className="text-amber-400"> · city unresolved</span>}
            </span>
          ) : event.venueAddress ? (
            <span>{event.venueAddress} <span className="text-amber-400">(unresolved)</span></span>
          ) : (
            <span className="italic">No venue info</span>
          )}
        </div>
        {canSave && (
          <button
            onClick={() => onSave(event)}
            disabled={saving}
            className="btn btn-sm bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border-0 shrink-0"
          >
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Save to DB
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Post preview card
// ──────────────────────────────────────────────────────────────────────────────

function PostCard({ post }: { post: CrawledPostPreview }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card border border-dark-border space-y-2">
      <div className="flex items-start gap-3">
        {post.imageUrls[0] && (
          <img
            src={post.imageDataUrl ?? proxyCdnUrl(post.imageUrls[0])}
            alt="post"
            title={post.imageUrls[0]}
            className="w-12 h-12 rounded-lg object-cover shrink-0 bg-dark-border"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-dark-muted">
            {post.postedAt ? new Date(post.postedAt).toLocaleDateString() : "Unknown date"}
          </p>
          <p className="text-sm line-clamp-2">{post.text || <em className="text-dark-muted">No caption</em>}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={post.postUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-sm p-2"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          {post.text && (
            <button onClick={() => setOpen((o) => !o)} className="btn btn-ghost btn-sm p-2">
              {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="pt-2 border-t border-dark-border text-xs text-neutral-300 whitespace-pre-wrap">
              {post.text}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Debug panel
// ──────────────────────────────────────────────────────────────────────────────

function DebugPanel() {
  const [platform, setPlatform] = useState<SocialPlatform>("instagram");
  const [identifier, setIdentifier] = useState("");
  const [maxPosts, setMaxPosts] = useState(10);
  const [maxAgeDays, setMaxAgeDays] = useState(30);
  const [extractEvents, setExtractEvents] = useState(true);
  const [resolveVenues, setResolveVenues] = useState(true);
  const [activeTab, setActiveTab] = useState<"events" | "posts">("events");
  // Track saved events: sourcePostUrl → saved eventId
  const [savedMap, setSavedMap] = useState<Record<string, string>>({});
  const [savingUrl, setSavingUrl] = useState<string | null>(null);

  const debug = useDebugSocialCrawl();
  const saveEvent = useSaveDebugEvent();

  const handleRun = (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setSavedMap({});
    debug.mutate({
      platform,
      pageIdentifier: identifier.trim(),
      maxPosts,
      maxPostAgeDays: maxAgeDays,
      extractEvents,
      resolveVenues,
    });
  };

  const handleSave = async (event: ExtractedEventPreview) => {
    if (!event.resolvedVenue || !event.date || !event.time) return;
    setSavingUrl(event.sourcePostUrl);
    try {
      const res = await saveEvent.mutateAsync({
        name: event.name,
        date: event.date,
        time: event.time,
        endTime: event.endTime,
        description: event.description,
        types: event.types,
        priceCents: event.priceCents,
        coverImageUrl: event.coverImageUrl,
        links: event.links,
        sourcePostUrl: event.sourcePostUrl,
        resolvedVenue: event.resolvedVenue,
      });
      if (res.success && res.data) {
        setSavedMap((m) => ({ ...m, [event.sourcePostUrl]: res.data!.eventId }));
      }
    } finally {
      setSavingUrl(null);
    }
  };

  const result = debug.data?.data;
  const eventCount = result?.extractedEvents?.filter((e) => e.isEvent).length ?? 0;
  const postCount = result?.posts?.length ?? 0;

  return (
    <div className="space-y-4">
      <form onSubmit={handleRun} className="card space-y-4">
        <h3 className="font-medium text-sm flex items-center gap-2">
          <Bug className="w-4 h-4 text-amber-400" />
          Debug crawl
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-dark-muted">Platform</span>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
              className="input input-sm"
            >
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 col-span-2 md:col-span-1">
            <span className="text-xs text-dark-muted">
              {platform === "instagram" ? "Username" : "Page ID / username"}
            </span>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={platform === "instagram" ? "outclose.nyc" : "OutcloseNYC"}
              className="input input-sm"
              required
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-dark-muted">Max posts</span>
            <input
              type="number"
              min={1}
              max={50}
              value={maxPosts}
              onChange={(e) => setMaxPosts(Number(e.target.value))}
              className="input input-sm"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-dark-muted">Max age (days)</span>
            <input
              type="number"
              min={1}
              max={365}
              value={maxAgeDays}
              onChange={(e) => setMaxAgeDays(Number(e.target.value))}
              className="input input-sm"
            />
          </label>
        </div>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={extractEvents}
                onChange={(e) => setExtractEvents(e.target.checked)}
                className="checkbox checkbox-sm"
              />
              Run Gemini event extraction
            </label>
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${!extractEvents ? "opacity-40 pointer-events-none" : ""}`}>
              <input
                type="checkbox"
                checked={resolveVenues}
                onChange={(e) => setResolveVenues(e.target.checked)}
                disabled={!extractEvents}
                className="checkbox checkbox-sm"
              />
              Resolve venues via Google Maps
            </label>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={debug.isPending}
              className="btn btn-sm btn-primary"
            >
              {debug.isPending ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" />
              ) : (
                <Search className="w-3.5 h-3.5 mr-1.5" />
              )}
              Run debug crawl
            </button>
          </div>
        </div>
      </form>

      {debug.error && (
        <div className="card border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {debug.error.message}
        </div>
      )}

      {result && (
        <div className="space-y-3">
          {result.mapsConfigured === false && (
            <div className="card border border-amber-500/30 text-amber-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Google Maps API not configured — venue geocoding and city resolution are unavailable
            </div>
          )}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveTab("events")}
              className={`text-sm font-medium pb-1 border-b-2 transition-colors ${
                activeTab === "events"
                  ? "border-primary-500 text-primary-400"
                  : "border-transparent text-dark-muted"
              }`}
            >
              Events ({eventCount})
            </button>
            <button
              onClick={() => setActiveTab("posts")}
              className={`text-sm font-medium pb-1 border-b-2 transition-colors ${
                activeTab === "posts"
                  ? "border-primary-500 text-primary-400"
                  : "border-transparent text-dark-muted"
              }`}
            >
              Raw posts ({postCount})
            </button>
          </div>

          <AnimatePresence mode="wait">
            {activeTab === "events" && (
              <motion.div
                key="events"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-2"
              >
                {result.extractedEvents.length === 0 ? (
                  <p className="text-sm text-dark-muted text-center py-6">No events extracted</p>
                ) : (
                  result.extractedEvents.map((evt, i) => (
                    <ExtractedEventCard
                      key={i}
                      event={evt}
                      onSave={handleSave}
                      saving={savingUrl === evt.sourcePostUrl}
                      savedId={savedMap[evt.sourcePostUrl] ?? null}
                    />
                  ))
                )}
              </motion.div>
            )}
            {activeTab === "posts" && (
              <motion.div
                key="posts"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-2"
              >
                {result.posts.length === 0 ? (
                  <p className="text-sm text-dark-muted text-center py-6">No posts found</p>
                ) : (
                  result.posts.map((post) => <PostCard key={post.id} post={post} />)
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────────────

export default function AdminSocialCrawling() {
  const { data, isLoading } = useAdminSocialCrawlPages();
  const [addMode, setAddMode] = useState<"single" | "batch" | null>(null);
  const [activeSection, setActiveSection] = useState<"pages" | "debug">("pages");

  const pages: SocialCrawlPage[] = data?.data ?? [];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Social Crawling</h1>
          <p className="text-sm text-dark-muted mt-0.5">
            Automatically discover events from Instagram &amp; Facebook pages
          </p>
        </div>
        <div className="flex gap-1 bg-dark-surface rounded-lg p-1">
          <button
            onClick={() => setActiveSection("pages")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeSection === "pages"
                ? "bg-dark-border text-white"
                : "text-dark-muted hover:text-white"
            }`}
          >
            Pages
          </button>
          <button
            onClick={() => setActiveSection("debug")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activeSection === "debug"
                ? "bg-dark-border text-white"
                : "text-dark-muted hover:text-white"
            }`}
          >
            Debug
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeSection === "pages" && (
          <motion.div
            key="pages"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {/* Add page */}
            <div className="card">
              <div className="flex items-center justify-between">
                <h2 className="font-medium text-sm">Tracked pages</h2>
                <div className="flex gap-1">
                  <button
                    onClick={() => setAddMode(addMode === "single" ? null : "single")}
                    className="btn btn-sm btn-ghost gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add page
                  </button>
                  <button
                    onClick={() => setAddMode(addMode === "batch" ? null : "batch")}
                    className="btn btn-sm btn-ghost gap-1"
                  >
                    <Layers className="w-4 h-4" />
                    Batch import
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {addMode && (
                  <motion.div
                    key={addMode}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 pt-4 border-t border-dark-border">
                      {addMode === "single"
                        ? <AddPageForm onDone={() => setAddMode(null)} />
                        : <BatchAddForm onDone={() => setAddMode(null)} />}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {isLoading && (
              <div className="flex justify-center py-10">
                <RefreshCw className="w-5 h-5 animate-spin text-dark-muted" />
              </div>
            )}

            {!isLoading && pages.length === 0 && (
              <div className="card text-center py-10 text-dark-muted text-sm">
                No pages tracked yet. Add a page above to start crawling.
              </div>
            )}

            <div className="space-y-2">
              {pages.map((page: SocialCrawlPage) => (
                <PageRow key={page.id} page={page} />
              ))}
            </div>

            {pages.length > 0 && (
              <p className="text-xs text-dark-muted text-center">
                {pages.filter((p: SocialCrawlPage) => p.isActive).length} of {pages.length} pages active ·
                Crawls run manually or via scheduled job
              </p>
            )}
          </motion.div>
        )}

        {activeSection === "debug" && (
          <motion.div
            key="debug"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <DebugPanel />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
